import { useEffect, useEffectEvent } from "react";
import { useLocation, useSearchParams } from "react-router";
import { useSessionStorage } from "usehooks-ts";

interface RouteVisit {
  route: string;
  timestamp: number;
  params?: Record<string, string>;
}

interface SessionData {
  sessionId: string | null;
  visitedRoutes: RouteVisit[];
  currentFlow: "market-to-order" | "direct-to-storefront" | null;
  journeySource?: ConsolidatedOrderingJourneySource;
}

export type ConsolidatedOrderingJourneySource =
  | "market"
  | "suppliers-list"
  | "direct"
  | "unknown";

const SESSION_TRACKING_KEY = "session-tracking";
const EMPTY_SESSION: SessionData = {
  sessionId: null,
  visitedRoutes: [],
  currentFlow: null,
};

// Helper function to determine if route should preserve session
const isSessionPreservingRoute = (pathname: string): boolean => {
  // Discovery/Market routes
  if (pathname.startsWith("/discover") || pathname.startsWith("/spotlight")) {
    return true;
  }

  // Storefront and checkout routes pattern: /suppliers/:supplierIdOrUrlId/order*
  const storefrontPattern = /^\/suppliers\/[^/]+\/order/;
  if (storefrontPattern.test(pathname)) {
    return true;
  }

  if (pathname.startsWith("/checkout/unified/")) {
    return true;
  }

  return false;
};

const shouldRefreshSession = (
  pathname: string,
  hasExistingSessionId: boolean,
): boolean => {
  // Refresh on market home page
  if (pathname === "/discover") {
    return true;
  }

  // Refresh on storefront if no existing sessionId (i.e not come from market)
  const storefrontPattern = /^\/suppliers\/[^/]+\/order$/;
  return storefrontPattern.test(pathname) && !hasExistingSessionId;
};

export const useMarketStorefrontSessionTracking = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [sessionData, setSessionData] = useSessionStorage<SessionData>(
    SESSION_TRACKING_KEY,
    EMPTY_SESSION,
  );

  const initializeSession = (route: string) => {
    const newSessionId = window.crypto.randomUUID();

    setSessionData({
      sessionId: newSessionId,
      visitedRoutes: [
        {
          route,
          timestamp: Date.now(),
          params: {},
        },
      ],
      currentFlow: route.startsWith("/discover")
        ? "market-to-order"
        : "direct-to-storefront",
      journeySource:
        route === "/discover/delivered-by-sellar"
          ? "direct"
          : route.startsWith("/discover")
            ? "market"
            : "direct",
    });
  };

  const clearSession = () => {
    setSessionData({
      sessionId: null,
      visitedRoutes: [],
      currentFlow: null,
      journeySource: undefined,
    });
  };

  const trackRouteVisit = (
    route: string,
    params: Record<string, string> = {},
  ) => {
    if (!sessionData.sessionId) {
      initializeSession(route);
      return;
    }

    setSessionData((current: SessionData) => ({
      ...current,
      visitedRoutes: [
        ...current.visitedRoutes,
        {
          route,
          timestamp: Date.now(),
          params,
        },
      ],
    }));
  };

  const refreshSession = () => {
    const currentRoute = window.location.pathname;
    initializeSession(currentRoute);
  };

  const searchParamsString = searchParams.toString();

  const trackCurrentRoute = useEffectEvent(() => {
    const currentRoute = location.pathname;
    const hasExistingSessionId = !!sessionData.sessionId;

    // Check if current route should preserve session
    if (!isSessionPreservingRoute(currentRoute)) {
      // Clear session immediately when navigating away from tracked flows
      if (sessionData.sessionId) {
        clearSession();
      }
      return; // Don't track this route
    }

    // Check if we need to refresh session (for tracked routes)
    if (shouldRefreshSession(currentRoute, hasExistingSessionId)) {
      refreshSession();
      return; // refreshSession will create a new session and track the route
    }

    // Track the route visit if we have a session
    if (sessionData.sessionId) {
      const params = Object.fromEntries(searchParams);
      trackRouteVisit(currentRoute, params);
    }
  });

  useEffect(() => {
    trackCurrentRoute();
  }, [location.pathname, searchParamsString]);

  // Return the current session data for components that might need it
  return {
    sessionId: sessionData.sessionId,
    visitedRoutes: sessionData.visitedRoutes,
    currentFlow: sessionData.currentFlow,
    isTracking: isSessionPreservingRoute(location.pathname),
    getSessionHistory: () => sessionData.visitedRoutes,
  };
};

/**
 * Read-only hook to get the current session ID without any side effects.
 * Use this in components that only need to read the session ID.
 * For initialization and tracking, use useMarketStorefrontSessionTracking instead.
 */
export const getMarketStorefrontSession = () => {
  try {
    const sessionDataString =
      window.sessionStorage.getItem(SESSION_TRACKING_KEY);
    if (sessionDataString) {
      const sessionData = JSON.parse(sessionDataString) as SessionData;
      return {
        sessionId: sessionData.sessionId,
        journeySource: sessionData.journeySource ?? "unknown",
        getSessionHistory: () => sessionData.visitedRoutes,
      };
    }
    return {
      sessionId: null,
      journeySource: "unknown" as const,
      getSessionHistory: () => [],
    };
  } catch {
    return {
      sessionId: null,
      journeySource: "unknown" as const,
      getSessionHistory: () => [],
    };
  }
};

export const useMarketStorefrontSessionId = () => {
  const [sessionData, setSessionData] = useSessionStorage<SessionData>(
    SESSION_TRACKING_KEY,
    EMPTY_SESSION,
  );

  const updateJourneySource = (
    journeySource: ConsolidatedOrderingJourneySource,
  ) => {
    const nextSession = {
      ...sessionData,
      sessionId: sessionData.sessionId ?? window.crypto.randomUUID(),
      journeySource,
    };
    setSessionData(nextSession);
    return nextSession;
  };

  return {
    sessionId: sessionData.sessionId,
    journeySource: sessionData.journeySource ?? ("unknown" as const),
    getSessionHistory: () => sessionData.visitedRoutes,
    updateJourneySource,
  };
};
